import { buildLiveSafetyCasePath } from '../../utils/acceptanceHierarchyNavigation';
import { buildRiskPatternReviewPath } from '../incidents/riskPatternWorkflowReviewContext';
import {
  CORRECTIVE_ACTION_TYPE_OPTIONS,
  correctiveActionTypeLabel,
} from '../incidents/groupedCorrectiveActionPresentation';
import {
  formatControlStatusLabel,
  mapControlStatusFromApi,
  operatorVisibleNote,
  splitControlNoteAndReview,
} from './safetyCaseControlsPresenter';
import { pickSharedWorkflowControl } from './investigateControlLink';
import { formatSiteMonthDay, isCalendarDateOnly } from '../siteTime/siteTime';
import { formatObservationCount } from '../copy/countNoun';
import { resolveConfiguredZoneLabel } from '../customerLabels/customerFacingControlLabel';

function asText(value: unknown): string {
  return String(value || '').trim();
}

function normalize(value: unknown): string {
  return asText(value).toLowerCase();
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function looksLikeInternalId(value: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value)
    || /^(action|ca|wf|rec)-/i.test(value);
}

function looksLikeJsonBlob(value: string): boolean {
  if (!value.startsWith('{') && !value.startsWith('[')) return false;
  return Boolean(splitControlNoteAndReview(value).rawJson);
}

export function usefulControlText(value: unknown): string {
  const text = operatorVisibleNote(value) || asText(value);
  if (!text || looksLikeJsonBlob(text) || looksLikeInternalId(text)) return '';
  return text;
}

type LinkedRecommendation = {
  linked_corrective_action?: unknown;
  linked_corrective_actions?: unknown[];
};

function actionLooksRecorded(value: unknown): boolean {
  const record = asRecord(value);
  if (!record) return false;
  return Boolean(
    asText(record.action_id)
    || usefulControlText(record.title)
    || usefulControlText(record.description)
    || asText(record.title)
    || asText(record.description),
  );
}

/** Same shared CA record Investigate/Controls use: linked action, else workflow list. */
export function pickOverviewControlRecord(input: {
  actions?: unknown;
  recommendations?: LinkedRecommendation[] | null;
}): Record<string, unknown> | null {
  const actions = Array.isArray(input.actions) ? input.actions : [];
  const recommendations = Array.isArray(input.recommendations) ? input.recommendations : [];
  for (const recommendation of recommendations) {
    const linked = asRecord(recommendation.linked_corrective_action);
    if (linked && actionLooksRecorded(linked)) {
      const linkedId = asText(linked.action_id);
      const match = linkedId
        ? actions.find((action) => asText(asRecord(action)?.action_id) === linkedId)
        : undefined;
      return match ? { ...linked, ...(asRecord(match) || {}) } : linked;
    }
    const extras = Array.isArray(recommendation.linked_corrective_actions)
      ? recommendation.linked_corrective_actions
      : [];
    const extra = extras.find(actionLooksRecorded);
    if (extra) return asRecord(extra);
  }
  const recorded = actions.find(actionLooksRecorded);
  if (recorded) return asRecord(recorded);
  return asRecord(pickSharedWorkflowControl({
    active_actions: actions as Array<{ action_id?: string | null; title?: string | null; description?: string | null }>,
    completed_actions: [],
  }));
}

export function formatOverviewDueDate(value: unknown, timeZone?: string | null): string | null {
  const text = asText(value);
  if (!text) return null;
  if (isCalendarDateOnly(text)) return formatSiteMonthDay(text, timeZone) || text;
  return formatSiteMonthDay(text, timeZone) || text;
}

export function formatVerifiedObservationCount(count: number): string {
  return formatObservationCount(count);
}

export type OverviewControlCase = 'recorded' | 'recorded_without_details' | 'none_in_force' | 'missing';

export type OverviewControlView = {
  case: OverviewControlCase;
  title: string;
  category?: string | null;
  detail?: string;
  owner?: string | null;
  status?: string | null;
  due?: string | null;
  date?: string | null;
  dateKind?: 'due' | 'recorded';
  showRecordControlCta: boolean;
  showViewUpdateLink: boolean;
};

function isControlCategoryLabel(value: string): boolean {
  const key = normalize(value);
  if (!key) return false;
  return CORRECTIVE_ACTION_TYPE_OPTIONS.some((option) => (
    normalize(option.label) === key || option.value === key
  ));
}

function resolveControlCategory(record: Record<string, unknown> | null): string {
  const fromType = asText(record?.action_type);
  if (fromType) {
    const labeled = correctiveActionTypeLabel(fromType);
    if (labeled && labeled !== 'Not set') return labeled;
  }
  const title = usefulControlText(record?.title);
  return isControlCategoryLabel(title) ? title : '';
}

export function resolveOverviewControlView(input: {
  actions?: unknown;
  recommendations?: LinkedRecommendation[] | null;
  lifecycleControlRecorded?: boolean;
  currentControlApplicable?: boolean | null;
}): OverviewControlView {
  if (input.currentControlApplicable === false) {
    const historical = pickOverviewControlRecord(input) || input.lifecycleControlRecorded;
    if (historical) {
      return {
        case: 'none_in_force',
        title: 'No control currently in force',
        detail: 'Historical Control on file remains available in Controls.',
        showRecordControlCta: false,
        showViewUpdateLink: true,
      };
    }
    return {
      case: 'missing',
      title: 'No control recorded yet',
      detail: 'Record what the site plans to do about this recurring safety issue.',
      showRecordControlCta: true,
      showViewUpdateLink: false,
    };
  }
  const record = pickOverviewControlRecord(input);
  const titleText = usefulControlText(record?.title);
  const descriptionText = usefulControlText(record?.description);
  const category = resolveControlCategory(record);
  const titleIsCategory = Boolean(titleText) && (
    isControlCategoryLabel(titleText)
    || (category && normalize(titleText) === normalize(category))
  );
  const actionTitle = (titleIsCategory ? descriptionText : titleText)
    || descriptionText
    || titleText;
  if (actionTitle) {
    const recordedAt = formatOverviewDueDate(
      record?.completed_at || record?.validated_at,
    );
    const due = formatOverviewDueDate(record?.due_date);
    const secondaryCategory = category && normalize(category) !== normalize(actionTitle)
      ? category
      : null;
    return {
      case: 'recorded',
      title: actionTitle,
      category: secondaryCategory,
      detail: (!titleIsCategory && descriptionText && descriptionText !== actionTitle)
        ? descriptionText
        : undefined,
      owner: asText(record?.assignee) || asText(record?.owner) || null,
      status: formatControlStatusLabel(mapControlStatusFromApi(record?.status)),
      due,
      date: recordedAt || due,
      dateKind: recordedAt ? 'recorded' : (due ? 'due' : undefined),
      showRecordControlCta: false,
      showViewUpdateLink: true,
    };
  }
  if (record || input.lifecycleControlRecorded) {
    return {
      case: 'recorded_without_details',
      title: 'Control recorded',
      detail: 'Details are available in Controls.',
      showRecordControlCta: false,
      showViewUpdateLink: true,
    };
  }
  return {
    case: 'missing',
    title: 'No control recorded yet',
    detail: 'Record what the site plans to do about this recurring safety issue.',
    showRecordControlCta: true,
    showViewUpdateLink: false,
  };
}

export type OverviewFollowUpKind =
  | 'baseline_establishment'
  | 'monitoring'
  | 'data_available'
  | 'still_observed'
  | 'lower_exposure';

export type OverviewFollowUpView = {
  kind: OverviewFollowUpKind;
  headline: string;
  sentence: string;
};

export type OverviewNextStepCtaKind = 'verify_observations' | 'establish_initial_baseline' | 'record_control';

export type OverviewNextStepKind = 'verify_observations' | 'establish_initial_baseline' | 'baseline_waiting' | 'record_control' | 'review_complete';

export type OverviewNextStepView = {
  kind: OverviewNextStepKind;
  line: string;
  detail?: string | null;
  systemStatusHeadline?: string | null;
  systemStatusDetail?: string | null;
  primaryCta: { kind: OverviewNextStepCtaKind; label: string } | null;
};

export type NextSafetyScanKind = 'baseline' | 'accumulating' | 'draft_not_ready' | 'ready';

export type OverviewNextSafetyScanView = {
  kind: NextSafetyScanKind;
  headline: string;
  sentence: string;
  readinessReason?: string | null;
  showOpenSafetyScanPrimary: boolean;
  showViewSafetyScanSecondary: boolean;
};

function asCount(value: unknown): number {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? Math.trunc(n) : 0;
}

export function resolveOverviewNextStep(input: {
  nextAction?: string | null;
  operationalState?: string | null;
  reviewPending?: number | null;
  reviewReviewed?: number | null;
  reviewStatus?: string | null;
}): OverviewNextStepView {
  const action = normalize(input.nextAction);
  const state = normalize(input.operationalState);
  const pendingCount = asCount(input.reviewPending);
  const reviewedCount = asCount(input.reviewReviewed);
  if (state === 'baseline_waiting') {
    return {
      kind: 'baseline_waiting',
      line: 'WAITING FOR NEXT CLEAN MEASUREMENT WINDOW',
      detail: 'Baseline establishment has started. The next eligible Safety Scan window will become the initial baseline.',
      systemStatusHeadline: 'BASELINE ESTABLISHMENT',
      systemStatusDetail: 'No Control is currently in force for this Safety Case.',
      primaryCta: null,
    };
  }
  if (action === 'verify_observations' || (
    action !== 'record_control'
    && action !== 'establish_initial_baseline'
    && pendingCount > 0
  )) {
    return {
      kind: 'verify_observations',
      line: 'NEXT STEP: VERIFY NEW OBSERVATIONS',
      primaryCta: {
        kind: 'verify_observations',
        label: pendingCount > 0 ? `Verify observations — ${pendingCount} pending` : 'Verify observations',
      },
    };
  }
  if (action === 'establish_initial_baseline') {
    return {
      kind: 'establish_initial_baseline',
      line: 'NEXT STEP: ESTABLISH INITIAL BASELINE',
      detail: 'Use the next clean Safety Scan window as the initial baseline for this new control lifecycle.',
      systemStatusHeadline: 'BASELINE ESTABLISHMENT',
      systemStatusDetail: 'No Control is currently in force for this Safety Case.',
      primaryCta: { kind: 'establish_initial_baseline', label: 'Establish Initial baseline' },
    };
  }
  if (action === 'record_control') {
    return {
      kind: 'record_control',
      line: 'NEXT STEP: RECORD WHAT THE SITE IS DOING ABOUT THIS ISSUE',
      primaryCta: { kind: 'record_control', label: 'Record control' },
    };
  }
  const total = reviewedCount + pendingCount;
  return {
    kind: 'review_complete',
    line: 'REVIEW COMPLETE',
    detail: total > 0
      ? `All ${formatVerifiedObservationCount(total)} verified. No further review action is required right now.`
      : 'No further review action is required right now.',
    systemStatusHeadline: 'EDGENTIK IS MONITORING',
    systemStatusDetail: 'Edgentik is continuing to monitor this condition for new activity.',
    primaryCta: null,
  };
}

export function resolveNextSafetyScanView(input: {
  nextAction?: string | null;
  operationalState?: string | null;
  reviewPending?: number | null;
  hasActiveDraft?: boolean;
  hasSignedScan?: boolean;
  signoffReady?: boolean;
  claimReadinessResolved?: number | null;
  claimReadinessTotal?: number | null;
  firstUnresolvedBlockerLabel?: string | null;
}): OverviewNextSafetyScanView | null {
  const action = normalize(input.nextAction);
  const state = normalize(input.operationalState);
  const pendingCount = asCount(input.reviewPending);
  if (state === 'baseline_waiting') {
    return {
      kind: 'baseline',
      headline: 'NEXT SAFETY SCAN',
      sentence: 'Waiting for the next clean measurement window.',
      showOpenSafetyScanPrimary: false,
      showViewSafetyScanSecondary: false,
    };
  }
  if (action === 'verify_observations' || action === 'record_control' || pendingCount > 0) {
    return null;
  }
  if (action === 'establish_initial_baseline') {
    return {
      kind: 'baseline',
      headline: 'NEXT SAFETY SCAN',
      sentence: 'Establish initial baseline when the next clean measurement window is ready.',
      showOpenSafetyScanPrimary: false,
      showViewSafetyScanSecondary: false,
    };
  }
  if (input.signoffReady && input.hasActiveDraft) {
    return {
      kind: 'ready',
      headline: 'NEXT SAFETY SCAN',
      sentence: 'This Safety Scan is ready to open.',
      showOpenSafetyScanPrimary: true,
      showViewSafetyScanSecondary: false,
    };
  }
  if (input.hasActiveDraft) {
    const resolved = Number(input.claimReadinessResolved);
    const total = Number(input.claimReadinessTotal);
    const checks = Number.isFinite(resolved) && Number.isFinite(total) && total > 0
      ? `${Math.trunc(resolved)}/${Math.trunc(total)} checks for this scan`
      : null;
    const blocker = asText(input.firstUnresolvedBlockerLabel);
    const readinessReason = blocker
      ? (checks ? `${blocker} · ${checks}` : blocker)
      : (checks
        ? `Safety Scan sign-off is not ready — ${checks}.`
        : 'Safety Scan sign-off is not ready.');
    return {
      kind: 'draft_not_ready',
      headline: 'NEXT SAFETY SCAN',
      sentence: 'A draft Safety Scan is in progress.',
      readinessReason,
      showOpenSafetyScanPrimary: false,
      showViewSafetyScanSecondary: true,
    };
  }
  return {
    kind: 'accumulating',
    headline: 'NEXT SAFETY SCAN',
    sentence: 'Follow-up evidence is accumulating.',
    showOpenSafetyScanPrimary: false,
    showViewSafetyScanSecondary: Boolean(input.hasSignedScan),
  };
}

const INSUFFICIENT_TOKENS = [
  'insufficient_data',
  'incomplete_data',
  'insufficient_observation_time',
  'accepted_insufficient',
  'pending',
];

const REDUCED_TOKENS = ['decreased', 'improved', 'reduction', 'lower'];
const CONTINUED_TOKENS = ['increased', 'worsened', 'worse', 'unchanged_high'];

function hasFollowUpData(input: {
  followupObservedCount?: unknown;
  directionalChange?: unknown;
  rateChangePercent?: unknown;
}): boolean {
  const directional = normalize(input.directionalChange);
  const hasDirection = Boolean(directional) && !['not_measured', 'pending', 'unknown', ''].includes(directional);
  const hasCount = input.followupObservedCount != null && asText(input.followupObservedCount) !== '';
  const hasRate = input.rateChangePercent != null && Number.isFinite(Number(input.rateChangePercent));
  return hasCount || hasDirection || hasRate;
}

export function resolveOverviewFollowUp(input: {
  measurementStatus?: unknown;
  measurementConfidence?: unknown;
  followupObservedCount?: unknown;
  directionalChange?: unknown;
  rateChangePercent?: unknown;
  resultLabel?: string | null;
  claimReady?: boolean;
  currentControlApplicable?: boolean | null;
  currentScanEstablished?: boolean | null;
  operationalState?: string | null;
}): OverviewFollowUpView {
  if (normalize(input.operationalState) === 'baseline_waiting') {
    return {
      kind: 'baseline_establishment',
      headline: 'Waiting for baseline evidence',
      sentence: 'Baseline establishment has started; no Control is currently in force.',
    };
  }
  if (input.currentControlApplicable === false && input.currentScanEstablished === false) {
    return {
      kind: 'baseline_establishment',
      headline: 'Ready to establish baseline',
      sentence: 'No Control is currently in force, so the next Safety Scan should establish the initial baseline.',
    };
  }
  const directional = normalize(input.directionalChange);
  const result = normalize(input.resultLabel);
  const tokens = [normalize(input.measurementStatus), normalize(input.measurementConfidence), result];
  const percent = input.rateChangePercent != null && Number.isFinite(Number(input.rateChangePercent))
    ? Number(input.rateChangePercent)
    : null;
  const reduced = (percent != null && percent > 0)
    || REDUCED_TOKENS.some((token) => directional.includes(token) || result.includes(token));
  const continued = (percent != null && percent < 0)
    || CONTINUED_TOKENS.some((token) => directional.includes(token) || result.includes(token))
    || result === 'worsened';

  if (reduced && !input.claimReady) {
    return {
      kind: 'lower_exposure',
      headline: 'Lower exposure observed',
      sentence: 'Follow-up observations show fewer events, but effectiveness has not been confirmed.',
    };
  }
  if (continued) {
    return {
      kind: 'still_observed',
      headline: 'Pattern still observed',
      sentence: 'The safety issue continues to appear in follow-up monitoring.',
    };
  }
  if (hasFollowUpData(input) && !input.claimReady) {
    return {
      kind: 'data_available',
      headline: 'Follow-up data available',
      sentence: 'Review the active draft Safety Scan to see whether the pattern is still occurring.',
    };
  }
  const pending = tokens.some((token) => INSUFFICIENT_TOKENS.some((marker) => token.includes(marker)))
    || !hasFollowUpData(input);
  if (pending) {
    return {
      kind: 'monitoring',
      headline: 'Monitoring continues',
      sentence: 'More observations are needed before the effect of the control can be assessed.',
    };
  }
  return {
    kind: 'data_available',
    headline: 'Follow-up data available',
    sentence: 'Review the active draft Safety Scan to see whether the pattern is still occurring.',
  };
}

export function buildOverviewPrepareClaimReadinessPath(input: {
  caseId: string;
  orgId?: string | null;
  siteId?: string | null;
  workflowId?: string | null;
  incidentId?: string | null;
  reportId?: string | null;
}): string {
  const base = buildLiveSafetyCasePath({
    caseId: input.caseId,
    orgId: input.orgId,
    siteId: input.siteId,
    workflowId: input.workflowId,
    incidentId: input.incidentId,
  });
  const [pathname, search] = base.split('?');
  const params = new URLSearchParams(search || '');
  params.set('tab', 'more');
  params.set('section', 'claim');
  const reportId = asText(input.reportId);
  if (reportId) params.set('report_id', reportId);
  return `${pathname}?${params.toString()}`;
}

export function buildOverviewWaitingWatchPath(input: {
  siteId?: string | null;
  workflowId?: string | null;
  incidentId?: string | null;
  zoneId?: string | null;
  cameraId?: string | null;
  deviceId?: string | null;
  riskType?: string | null;
}): string {
  return buildRiskPatternReviewPath({
    site_id: input.siteId,
    workflow_id: input.workflowId,
    representative_incident_id: input.incidentId,
    zone_id: input.zoneId,
    camera_id: input.cameraId,
    device_id: input.deviceId,
    risk_type: input.riskType,
  }, asText(input.siteId));
}

export function buildSafetyOverviewV2Path(siteId?: string | null): string {
  const site = asText(siteId);
  if (!site) return '/acceptance/overview-v2';
  const query = new URLSearchParams();
  query.set('site', site);
  return `/acceptance/overview-v2?${query.toString()}`;
}

/** Exact Pattern review from a Safety Case. Adds group_key so Incidents auto-opens the group. */
export function buildSafetyCasePatternReviewPath(input: {
  siteId?: string | null;
  workflowId?: string | null;
  incidentId?: string | null;
  zoneId?: string | null;
  cameraId?: string | null;
  deviceId?: string | null;
  riskType?: string | null;
}): string {
  const base = buildOverviewWaitingWatchPath(input);
  const workflowId = asText(input.workflowId);
  if (!workflowId) return '';
  const [pathname, search] = base.split('?');
  const params = new URLSearchParams(search || '');
  if (!params.get('group_key')) params.set('group_key', workflowId);
  return `${pathname}?${params.toString()}`;
}

/**
 * Signed-record href only when both record ID and revision are known.
 * Reuses the portfolio contract: persisted report ID is the records-route ID.
 * Never emits `/acceptance/reports/records/:id` without a revision.
 */
export function buildExactSignedRecordPath(input: {
  recordId?: string | null;
  revision?: unknown;
  orgId?: string | null;
  siteId?: string | null;
}): string | null {
  const recordId = asText(input.recordId);
  const revision = Number(input.revision);
  if (!recordId || !Number.isInteger(revision) || revision <= 0) return null;
  const query = new URLSearchParams();
  const orgId = asText(input.orgId);
  const siteId = asText(input.siteId);
  if (orgId) query.set('org_id', orgId);
  if (siteId) query.set('site_id', siteId);
  const suffix = query.toString();
  return `/acceptance/reports/records/${encodeURIComponent(recordId)}/revisions/${revision}${
    suffix ? `?${suffix}` : ''
  }`;
}

const GENERIC_PROBLEM_SUMMARY = 'confirmed safety problem managed over time';
const PEDESTRIAN_FORKLIFT_CONDITION_TYPES = new Set([
  'vehicle_pedestrian_interaction',
  'pedestrian_route_conflict',
  'forklift_route_conflict',
  'poor_separation_people_vehicles',
  'high_traffic_overlap_zone',
]);

function looksLikeRawPlaceId(value: string): boolean {
  return /^[a-z0-9]+(?:[_-][a-z0-9]+)*$/i.test(value) && !/\s/.test(value);
}

function humanizeToken(value: unknown): string {
  return asText(value).replace(/[_-]+/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function usefulStoredSummary(value: unknown): string {
  const text = asText(value);
  if (!text || looksLikeJsonBlob(text) || looksLikeInternalId(text)) return '';
  if (normalize(text).includes(GENERIC_PROBLEM_SUMMARY)) return '';
  return text;
}

function cameraPlaceAlias(cameraId?: string | null, cameraLabel?: string | null): string {
  const camera = asText(cameraId);
  if (normalize(camera) === 'cam_walkway_zone_demo') return 'Walkway / Warehouse Room B';
  const label = asText(cameraLabel);
  if (label && !looksLikeRawPlaceId(label) && !looksLikeInternalId(label)) return label;
  return '';
}

function cameraPlaceArea(cameraId?: string | null): string {
  return normalize(cameraId) === 'cam_walkway_zone_demo' ? 'Warehouse Room B' : '';
}

export function resolveOverviewPlaceLabel(input: {
  zoneId?: string | null;
  zoneLabel?: string | null;
  areaLabel?: string | null;
  cameraId?: string | null;
  cameraLabel?: string | null;
  locationLabel?: string | null;
}): string {
  const cameraAlias = cameraPlaceAlias(input.cameraId, input.cameraLabel);
  const area = asText(input.areaLabel) || cameraPlaceArea(input.cameraId);
  const locationCandidates = [
    asText(input.locationLabel),
    asText(input.zoneLabel),
  ];
  const namedLocation = locationCandidates.find((value) => (
    value && !looksLikeRawPlaceId(value) && !looksLikeInternalId(value)
  ));
  if (
    namedLocation
    && area
    && !looksLikeRawPlaceId(area)
    && !looksLikeInternalId(area)
    && !normalize(namedLocation).includes(normalize(area))
    && !normalize(area).includes(normalize(namedLocation))
  ) {
    return `${namedLocation} / ${area}`;
  }
  const candidates = [
    namedLocation,
    asText(input.areaLabel),
    cameraAlias,
  ];
  const named = candidates.find((value) => value && !looksLikeRawPlaceId(value) && !looksLikeInternalId(value));
  if (named) return named;
  return resolveConfiguredZoneLabel(input.zoneId)
    || asText(input.zoneId)
    || asText(input.zoneLabel)
    || asText(input.areaLabel);
}

export function formatOverviewWhereThisHappens(place?: string | null): string {
  const text = asText(place);
  if (!text) return '';
  if (/^where this keeps happening:/i.test(text)) return text;
  return `Where this keeps happening: ${text}`;
}

export function resolveScanRepresentativeIncidentId(reportOpen: unknown): string {
  const record = asRecord(reportOpen);
  const report = asRecord(record?.report);
  const anchor = asRecord(report?.workflow_anchor);
  return asText(anchor?.representative_incident_id)
    || asText(record?.replacement_representative_incident_id);
}

export function resolveOverviewEvidenceIncidentId(input: {
  scanRepresentativeIncidentId?: string | null;
  representativeIncidentId?: string | null;
  queueIncidentId?: string | null;
  queueNextIncidentId?: string | null;
}): string {
  return asText(input.scanRepresentativeIncidentId)
    || asText(input.queueIncidentId)
    || asText(input.queueNextIncidentId)
    || asText(input.representativeIncidentId);
}

export function formatOverviewProblemTitle(input: {
  riskType?: string | null;
  place?: string | null;
}): string {
  const risk = humanizeToken(input.riskType) || 'Safety case';
  const rawPlace = asText(input.place);
  const place = resolveConfiguredZoneLabel(rawPlace) || rawPlace;
  if (!place) return risk;
  if (/walkway/i.test(place)) return `${risk} — Walkway`;
  if (looksLikeRawPlaceId(place)) return `${risk} at ${place}`;
  return `${risk} — ${place}`;
}

function overviewRiskPhrase(riskType?: string | null): string {
  const key = normalize(riskType);
  if (key === 'near_miss') return 'near-miss';
  return key.replace(/[_-]+/g, ' ') || 'safety';
}

function hasPedestrianForkliftSignal(input: {
  riskType?: string | null;
  hazardCategory?: string | null;
  conditionType?: string | null;
  conditionSummary?: string | null;
  likelyContributing?: string | null;
  conditionTags?: string[] | null;
}): boolean {
  const conditionType = normalize(input.conditionType);
  if (PEDESTRIAN_FORKLIFT_CONDITION_TYPES.has(conditionType)) return true;
  const tags = Array.isArray(input.conditionTags) ? input.conditionTags : [];
  if (tags.some((tag) => /forklift|pedestrian|vehicle|route_overlap|repeated_crossing/.test(normalize(tag)))) {
    return true;
  }
  const blob = [
    input.riskType,
    input.hazardCategory,
    input.conditionType,
    input.conditionSummary,
    input.likelyContributing,
  ].map((value) => normalize(value)).join(' ');
  return /vehicle.?pedestrian|pedestrian.?forklift|forklift.?pedestrian|pedestrian_in_forklift|forklift_in_pedestrian|forklift_proximity/.test(blob)
    || ((/pedestrian|people|person/.test(blob)) && (/forklift|vehicle/.test(blob)));
}

function placeInPhrase(place: string): string {
  const friendly = resolveConfiguredZoneLabel(place) || place;
  if (!friendly) return 'this site';
  if (/walkway/i.test(friendly)) return 'the walkway zone';
  if (looksLikeRawPlaceId(friendly)) return friendly;
  return `the ${friendly} zone`;
}

export function formatOverviewProblemSummary(input: {
  riskType?: string | null;
  hazardCategory?: string | null;
  place?: string | null;
  conditionType?: string | null;
  conditionSummary?: string | null;
  likelyContributing?: string | null;
  conditionTags?: string[] | null;
}): string {
  const stored = usefulStoredSummary(input.conditionSummary);
  if (stored) return stored;
  const place = asText(input.place);
  const where = placeInPhrase(place);
  if (hasPedestrianForkliftSignal(input)) {
    return `Recurring pedestrian / forklift exposure in ${where}`;
  }
  const contributing = usefulStoredSummary(input.likelyContributing);
  if (contributing) {
    return `Recurring ${overviewRiskPhrase(input.riskType)} exposure in ${where}: ${contributing}`;
  }
  return `Recurring ${overviewRiskPhrase(input.riskType)} exposure in ${where}`;
}

export type OverviewLatestEvidenceView = {
  confirmedLine: string;
  reviewLine: string;
  linkedLine: string | null;
};

export function formatOverviewLatestEvidence(input: {
  confirmedCount?: number | null;
  reviewedCount?: number | null;
  candidateCount?: number | null;
  pendingCount?: number | null;
  linkedReportCount?: number | null;
  riskType?: string | null;
  hasActiveDraft?: boolean;
}): OverviewLatestEvidenceView | null {
  const reviewed = Number(input.reviewedCount);
  const candidates = Number(input.candidateCount);
  const confirmed = Number(input.confirmedCount);
  const pending = Number(input.pendingCount);
  const reviewedCount = Number.isFinite(reviewed) && reviewed > 0 ? Math.trunc(reviewed) : 0;
  const candidateCount = Number.isFinite(candidates) && candidates > 0 ? Math.trunc(candidates) : 0;
  const observations = candidateCount || reviewedCount;
  const confirmedCount = Number.isFinite(confirmed) && confirmed >= 0 ? Math.trunc(confirmed) : 0;
  const pendingCount = Number.isFinite(pending) && pending > 0 ? Math.trunc(pending) : 0;
  const riskPhrase = overviewRiskPhrase(input.riskType);
  const reviewTotal = candidateCount || reviewedCount;
  const linked = Number(input.linkedReportCount);
  const linkedCount = Number.isFinite(linked) && linked > 0 ? Math.trunc(linked) : 0;
  if (!input.hasActiveDraft) {
    return {
      confirmedLine: '',
      reviewLine: '',
      linkedLine: linkedCount > 0
        ? `${linkedCount} linked Safety Scan${linkedCount === 1 ? '' : 's'}`
        : null,
    };
  }
  if (!observations && !Number.isFinite(confirmed) && !linkedCount) return null;
  const reviewLine = !input.hasActiveDraft
    ? ''
    : !observations
    ? ''
    : (pendingCount > 0
      ? `${reviewedCount} reviewed · ${pendingCount} pending`
      : `${reviewTotal || reviewedCount} observations reviewed for the active draft Safety Scan`);
  return {
    confirmedLine: (observations || Number.isFinite(confirmed))
      ? `${confirmedCount} confirmed ${riskPhrase} observation${confirmedCount === 1 ? '' : 's'}`
      : '',
    reviewLine,
    linkedLine: linkedCount > 0
      ? `${linkedCount} linked Safety Scan${linkedCount === 1 ? '' : 's'}`
      : null,
  };
}
